import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Printer, User, LogOut, Phone, MapPin, Mail, ChevronDown, ClipboardList } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';

const Header: React.FC = () => {
  const { getTotalItems } = useCart();
  const { user, isAuthenticated, logout } = useAuth();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const itemCount = getTotalItems();

  const handleLogout = () => {
    logout();
    setIsUserMenuOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header>
      <div className="bg-keystone-blue text-white shadow-lg">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center">
              <img 
                src="/logos/logo-white.png" 
                alt="Keystone Sign Printing" 
                className="h-10"
              />
            </Link>
            
            <nav className="hidden md:flex space-x-8">
              <Link to="/products?category=Banners" className="hover:text-keystone-gold transition-colors">
                Banners
              </Link>
              <Link to="/products?category=Signs" className="hover:text-keystone-gold transition-colors">
                Signs
              </Link>
              <Link to="/products?category=Flags" className="hover:text-keystone-gold transition-colors">
                Flags
              </Link>
              <Link to="/products?category=Posters" className="hover:text-keystone-gold transition-colors">
                Posters
              </Link>
              <Link to="/blog" className="hover:text-keystone-gold transition-colors">
                Blog
              </Link>
              {isAuthenticated && user?.role === 'admin' && (
                <Link to="/admin" className="hover:text-keystone-gold transition-colors">
                  Admin
                </Link>
              )}
            </nav>

            <div className="flex items-center space-x-4">
              <Link to="/cart" className="flex items-center space-x-2 hover:text-keystone-gold transition-colors">
                <div className="relative">
                  <ShoppingCart className="h-6 w-6" />
                  {itemCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                      {itemCount}
                    </span>
                  )}
                </div>
                <span className="hidden sm:inline">Cart</span>
              </Link>

              {isAuthenticated ? (
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="flex items-center space-x-2 hover:text-keystone-gold transition-colors"
                  >
                    <User className="h-5 w-5" />
                    <span className="hidden sm:inline text-sm">
                      {user?.firstName} {user?.lastName}
                    </span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {isUserMenuOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-2 z-50">
                      <Link 
                        to="/profile" 
                        className="block px-4 py-2 text-gray-800 hover:bg-gray-100 transition-colors"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <div className="flex items-center space-x-2">
                          <User className="h-4 w-4" />
                          <span>My Profile</span>
                        </div>
                      </Link>
                      <Link 
                        to="/orders" 
                        className="block px-4 py-2 text-gray-800 hover:bg-gray-100 transition-colors"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <div className="flex items-center space-x-2">
                          <ClipboardList className="h-4 w-4" />
                          <span>My Orders</span>
                        </div>
                      </Link>
                      <hr className="my-2 border-gray-200" />
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-2 text-gray-800 hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center space-x-2">
                          <LogOut className="h-4 w-4" />
                          <span>Logout</span>
                        </div>
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center space-x-3">
                  <Link to="/login" className="hover:text-keystone-gold transition-colors text-sm">
                    Login
                  </Link>
                  <Link to="/register" className="bg-keystone-gold hover:bg-keystone-gold-600 text-keystone-blue px-3 py-1 rounded transition-colors text-sm font-semibold">
                    Register
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Contact Information Bar */}
      <div className="bg-keystone-gold">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center space-x-4 md:space-x-6 py-2 text-black font-bold text-sm">
            <div className="flex items-center space-x-2">
              <Phone className="h-4 w-4" />
              <a href="tel:+12677183596" className="hover:text-keystone-blue transition-colors">
                (267) 718-3596
              </a>
            </div>
            <div className="flex items-center space-x-2">
              <Mail className="h-4 w-4" />
              <a href="mailto:info@keystoneprinter.com" className="hover:text-keystone-blue transition-colors">
                info@keystoneprinter.com
              </a>
            </div>
            <div className="hidden lg:flex items-center space-x-2">
              <MapPin className="h-4 w-4" />
              <a 
                href="https://maps.google.com/?q=24+Green+Street+Lansdale+PA+19446" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:text-keystone-blue transition-colors"
              >
                24 Green Street, Lansdale, PA 19446
              </a>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;